import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

import { KBarProvider, type Action } from "kbar";
import { useNavigate } from "react-router-dom";

import { useUser } from "@components/user-context";
import { useAdminModalIsOpen } from "@components/v2/modals/admin-modal/admin-modal";
import { useUniversalBenchmarksModalContext } from "@features/universal-benchmarks";
import { useIsDarkMode } from "@hooks/use-is-dark-mode";
import { logEvent } from "@internal/analytics";

import { CommandDialog } from "./command-dialog";

const HIGH_PRIORITY = 999;

export const CommandPaletteContext = createContext<{
  unregisterActionCallbacks: () => void;
  addUnregisterActionCallback: (callback: () => void) => void;
}>({
  unregisterActionCallbacks: () => null,
  addUnregisterActionCallback: () => null,
});

interface Props {
  children: ReactNode;
}

export const CommandPalette = ({ children }: Props) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { openUniversalBenchmarksModal } = useUniversalBenchmarksModalContext();
  const [, setIsAdminModalOpen] = useAdminModalIsOpen();

  const [isDarkMode, setIsDarkMode] = useIsDarkMode();

  const unregisterActionCallbacksRef = useRef<(() => void)[]>([]);

  const addUnregisterActionCallback = useCallback((callback: () => void) => {
    unregisterActionCallbacksRef.current.push(callback);
  }, []);

  const unregisterActionCallbacks = useCallback(() => {
    unregisterActionCallbacksRef.current.forEach((callback) => callback());
    unregisterActionCallbacksRef.current = [];
  }, []);

  const actions = useMemo(() => {
    const perform = (path: string) => {
      logEvent("Command palette -> onSelect:Select Navigation", {
        action: path,
      });

      navigate(user.pathFromRoot(path));
    };

    let result: (Action & { isExternalLink?: boolean; isNew?: boolean })[] = [
      {
        id: "overview",
        name: "Overview",
        subtitle:
          "Understand your high-level performance insights with ability to visualise all your most important metrics and ad platforms",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-house"></i>,
        keywords: "overview dashboard home",
        perform: () =>
          perform(
            user.featureFlags.enableDashboardV2 ? "home/overview" : "overview",
          ),
        priority: HIGH_PRIORITY,
      },
      {
        id: "sales",
        name: "Sales",
        subtitle:
          "Effectively analyze your platform/campaign/adset/ads performance in fine grain detail",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-chart-line"></i>,
        keywords: "sales report mta",
        perform: () =>
          perform(
            user.featureFlags.enableDashboardV2 ? "attribution/sales" : "sales",
          ),
        priority: HIGH_PRIORITY,
      },
      {
        id: "customer-ltv",
        name: "Customer LTV",
        subtitle: "Discover your customer's lifetime value",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-chart-bar"></i>,
        keywords: "customer lifetime value",
        perform: () => perform("customers"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "orders",
        name: "Orders",
        subtitle: "Analyse your orders",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cart-shopping"></i>,
        keywords: "order",
        perform: () =>
          perform(
            user.featureFlags.enableDashboardV2
              ? "attribution/orders"
              : "orders",
          ),
        priority: HIGH_PRIORITY,
      },
    ];

    if (user.featureFlags.productAnalytics) {
      result.push({
        id: "product-analytics",
        name: "Product Analytics",
        subtitle: "Analyse your products",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-basket-shopping"></i>,
        keywords: "product analytics",
        perform: () =>
          perform(
            user.featureFlags.enableDashboardV2
              ? "attribution/product-analytics"
              : "product-analytics",
          ),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.enableCreativeAnalytics) {
      result.push({
        id: "creative-analytics",
        name: "Creative Analytics",
        subtitle: "All your ad creatives and guided metrics at your fingertips",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-image"></i>,
        keywords: "creative analytics image video",
        perform: () =>
          perform(
            user.featureFlags.enableDashboardV2
              ? "attribution/creative-analytics"
              : "creative-analytics",
          ),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.enableMetricsExplorer) {
      result.push({
        id: "metrics-explorer",
        name: "Metrics Explorer",
        subtitle:
          "Measure the relationship between two metrics using the Pearson correlation coefficient",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-telescope"></i>,
        keywords: "metric explorer correlation coefficient pearson",
        perform: () => perform("metrics-explorer"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.enableUniversalBenchmarks) {
      result.push({
        id: "profitability-benchmark",
        name: "Profitability Benchmarks",
        subtitle: "Set your benchmark to understand true profitability",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-dumbbell"></i>,
        keywords: "profitability benchmarks",
        perform: () => openUniversalBenchmarksModal(),
        priority: HIGH_PRIORITY,
        isNew: true,
      });
    }

    if (user.featureFlags.enableMMM) {
      result.push({
        id: "mmm",
        name: "MMM+",
        subtitle:
          "A media mix modeling platform, powered by revolutionary dashboards and bespoke machine learning models",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-compass"></i>,
        keywords: "mmm marketing mixed media modeling",
        perform: () => perform("mmm"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.enableDashboardV2) {
      result.push({
        id: "dark-mode",
        name: "Toggle Dark Mode",
        subtitle: "Toggle the page between light and dark mode",
        section: { name: "", priority: HIGH_PRIORITY },
        icon: (
          <i
            className={`fa-regular fa-sharp ${
              isDarkMode ? "fa-moon" : "fa-sun"
            }`}
          ></i>
        ),
        keywords: "dark mode toggle night day color scheme theme",
        perform: () => setIsDarkMode((prevIsDarkMode) => !prevIsDarkMode),
        priority: HIGH_PRIORITY,
      });
    }

    if (
      user.displayUserId === "dd62a9c5-ca2d-4231-a4bf-0cb5acc56c49" &&
      user.featureFlags.showFarmstandPurchases
    ) {
      result.push({
        id: "farmstand-purchases",
        name: "Farmstand purchases",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-store" />,
        keywords: "farmstand purchase",
        perform: () => perform("farmstand-purchases"),
        priority: HIGH_PRIORITY,
      });
    }

    result.push({
      id: "breakdowns",
      name: "Breakdowns",
      subtitle: "Manage your campaign/adset/ad rules and breakdowns",
      section: { name: "Navigation", priority: HIGH_PRIORITY },
      keywords: "breakdown label",
      icon: <i className="fa-regular fa-sharp fa-list-tree"></i>,
      priority: HIGH_PRIORITY,
    });

    result.push({
      id: "manage-breakdowns",
      parent: "breakdowns",
      name: "Manage breakdowns",
      subtitle: "Manage automatic label rules",
      section: { name: "Navigation", priority: HIGH_PRIORITY },
      icon: <i className="fa-regular fa-sharp fa-list-tree"></i>,
      keywords: "breakdown label",
      perform: () => perform("breakdowns"),
      priority: HIGH_PRIORITY,
    });

    result.push({
      id: "label-assignment",
      parent: "breakdowns",
      name: "Label assignment",
      subtitle: "Manage label assignment",
      section: { name: "Navigation", priority: HIGH_PRIORITY },
      icon: <i className="fa-regular fa-sharp fa-list-tree"></i>,
      keywords: "breakdown label",
      perform: () => perform("label-assignment"),
      priority: HIGH_PRIORITY,
    });

    result = [
      ...result,
      {
        id: "model-comparison",
        name: "Model comparison",
        subtitle: "Compare different attribution models",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-ruler"></i>,
        keywords: "model comparison",
        perform: () => perform("model-comparison"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "customer-paths",
        name: "Customer paths",
        subtitle: "Understand your true customer journey",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-road"></i>,
        keywords: "customer path",
        perform: () => perform("customers/paths"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "documents",
        name: "Documents",
        subtitle: "Download exports and other documents",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-file"></i>,
        keywords: "documents files uploads downloads",
        perform: () => perform("customers/documents"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "guides",
        name: "Guides",
        subtitle:
          "Learn about redirect URLs, URL builder, naming conventions and watch educational videos",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-square-info"></i>,
        keywords: "guides",
        priority: HIGH_PRIORITY,
      },
      {
        id: "redirect-urls",
        parent: "guides",
        name: "Redirect URLs Guide",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-square-info"></i>,
        keywords: "redirect url guide",
        perform: () => perform("guides/redirect-urls"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "generic-url-builder",
        parent: "guides",
        name: "Generic URL builder",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-square-info"></i>,
        keywords: "generic url builder",
        perform: () => perform("guides/generic-url-builder"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "naming-conventions-manual",
        parent: "guides",
        name: "Naming conventions manual",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-square-info"></i>,
        keywords: "naming manual",
        perform: () => perform("guides/naming-conventions-manual"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "educational-videos",
        parent: "guides",
        name: "Educational videos",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-square-info"></i>,
        keywords: "education video",
        perform: () => perform("guides/educational-videos"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings",
        name: "Settings",
        subtitle: "Manage and configure your account",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config",
        priority: HIGH_PRIORITY,
      },

      {
        id: "settings-profile",
        parent: "settings",
        name: "Profile",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config profile",
        perform: () => perform("settings/profile"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-billing",
        parent: "settings",
        name: "Billing",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config billing payment",
        perform: () => perform("settings/billing"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-account-managers",
        parent: "settings",
        name: "Account managers",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config account manager",
        perform: () => perform("settings/account-managers"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-email-notifications",
        parent: "settings",
        name: "Email notification settings",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config email notification",
        perform: () => perform("settings/email-notifications"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-currency",
        parent: "settings",
        name: "Currency",
        subtitle: "Update the currency displayed in the app",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config currency",
        perform: () => perform("settings/currency"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-excluded-orders",
        parent: "settings",
        name: "Order tag exclusions",
        subtitle:
          "Use order tags to exclude certain purchases from attribution",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config tag exclusion",
        perform: () => perform("settings/excluded-orders"),
        priority: HIGH_PRIORITY,
      },

      ...(user.featureFlags.pixelDiagnosticsPage
        ? [
            {
              id: "diagonostics",
              name: "Data Diagnostics",
              parent: "settings",
              subtitle:
                "Diagnose your Pixel, Orders API and Spend API tracking and errors",
              section: { name: "Navigation", priority: HIGH_PRIORITY },
              icon: <i className="fa-regular fa-sharp fa-cog"></i>,
              keywords:
                "setting config data pixel validation diagnostics tracking",
              perform: () => perform("settings/tracking-events"),
              priority: HIGH_PRIORITY,
            },
          ]
        : []),

      ...(user.featureFlags.enableShopInclusionByOrderTag
        ? [
            {
              id: "settings-tag-tiktok-orders",
              parent: "settings",
              name: "Tag TikTok Shops Orders",
              subtitle:
                "Use order tags to tag certain purchases as TikTok Shops purchases",
              section: { name: "Navigation", priority: HIGH_PRIORITY },
              icon: <i className="fa-regular fa-sharp fa-cog"></i>,
              keywords: "setting config tag inclusion tiktok",
              perform: () => perform("settings/tik-tok-shops-order-tags"),
              priority: HIGH_PRIORITY,
            },

            {
              id: "settings-tag-meta-orders",
              parent: "settings",
              name: "Tag Meta Shops Orders",
              subtitle:
                "Use order tags to tag certain purchases as Meta Shops purchases",
              section: { name: "Navigation", priority: HIGH_PRIORITY },
              icon: <i className="fa-regular fa-sharp fa-cog"></i>,
              keywords: "setting config tag inclusion meta",
              perform: () => perform("settings/meta-shops-order-tags"),
              priority: HIGH_PRIORITY,
            },
          ]
        : []),
      {
        id: "settings-data-spreadsheets",
        parent: "settings",
        name: "Data import spreadsheets",
        subtitle: "Import spend and influencer data",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords:
          "setting config data import spreadsheet custom spend influencer",
        perform: () => perform("settings/data-spreadsheets"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-custom-annotations",
        parent: "settings",
        name: "Custom annotations",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config custom annotation",
        perform: () => perform("settings/custom-annotations"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-tracking-script-tags",
        parent: "settings",
        name: "Northbeam Pixel and Event Tracking",
        subtitle:
          "Instructions to add Northbeam's Pixel and any other relevant event tracking scripts",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config tracking script tag",
        perform: () => perform("settings/tracking-script-tags"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-utm-guide",
        parent: "settings",
        name: "UTM Guide",
        subtitle: "Add UTM tracking for each platform you have ads on",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config utm guide",
        perform: () => perform("settings/utm-guide"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-google-analytics-parameters-exclusions",
        parent: "settings",
        name: "Google Analytics parameter exclusions",
        subtitle:
          "Remove Northbeam`s UTM Parameters from your Google Analytics",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config google parameter exclusion",
        perform: () =>
          perform("settings/google-analytics-parameters-exclusions"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-dns-entry",
        parent: "settings",
        name: "Setup DNS entry",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config dns",
        perform: () => perform("settings/dns-entry"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-connections",
        parent: "settings",
        name: "Connections",
        subtitle:
          "Connect your platform/storefront accounts to Northbeam (Shopify, Meta, TikTok, Snapchat, Microsoft, etc..) ",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords:
          "setting config connection platform facebook fb google shopify tiktok snapchat microsoft integrations",
        perform: () => perform("settings/connections"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-additional-managed-domains",
        parent: "settings",
        name: "Additional managed domains",
        subtitle: "Set additional domains",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config domain",
        perform: () => perform("settings/additional-managed-domains"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "settings-api-keys",
        parent: "settings",
        name: "API keys",
        subtitle: "Manage your API keys",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config api key",
        perform: () => perform("settings/api-keys"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "alerts",
        name: "Alerts",
        subtitle: "Discover and configure alerts and maintenance items",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-bell"></i>,
        keywords: "alert",
        priority: HIGH_PRIORITY,
      },
      {
        id: "alerts-incidents",
        parent: "alerts",
        name: "Incidents",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-bell"></i>,
        keywords: "alert incident",
        perform: () => perform("monitor/incidents"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "alerts-maintenance-items",
        parent: "alerts",
        name: "Maintenance items",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-bell"></i>,
        keywords: "alert maintenance",
        perform: () => perform("monitor/maintenance-items"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "alerts-configure-alerts",
        parent: "alerts",
        name: "Configure alerts",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-bell"></i>,
        keywords: "alert",
        perform: () => perform("monitor"),
        priority: HIGH_PRIORITY,
      },
      {
        id: "help-page",
        name: "Help",
        subtitle: "Read Northbeam Technical Guides or reach out to our team",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-circle-question"></i>,
        keywords: "help support",
        perform: () => {
          const path =
            "https://docs.northbeam.io/docs/getting-started-with-northbeam";

          logEvent("Command palette -> onSelect:Select Navigation", {
            action: path,
          });

          window.open(path, "_blank", "noopener,noreferrer");
        },
        priority: HIGH_PRIORITY,
        isExternalLink: true,
      },
    ];

    if (user.featureFlags.enableDataExportManagementPage) {
      result.push({
        id: "settings-manage-exports",
        parent: "settings",
        name: "Manage exports",
        subtitle: "Manage your scheduled data exports from the Sales Page",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config data export",
        perform: () => perform("settings/manage-exports"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.userSettingsMenuItem) {
      result.push({
        id: "settings-user-settings",
        parent: "settings",
        name: "User settings",
        subtitle: "Reset your password",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config user profile account",
        perform: () => perform("settings/user-settings"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.showSubscriptionOrderTags) {
      result.push({
        id: "settings-subscription-order-tags",
        parent: "settings",
        name: "Subscription order tags",
        subtitle:
          "Map any subscription order tags to Northbeam's subscription categories",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config subscription order tag",
        perform: () => perform("settings/subscription-order-tags"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.enquireLabsIntegration) {
      result.push({
        id: "settings-enquire-labs-integration",
        parent: "settings",
        name: "Integrate with Enquire Labs",
        subtitle: "Integrate with Enquire Post-Purchase surveys",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config enquire lab",
        perform: () => perform("settings/enquire-labs-integration"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.showProcessingSettings) {
      result.push({
        id: "settings-model-settings",
        parent: "settings",
        name: "Model Settings",
        subtitle: "Update your model settings",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config model",
        perform: () => perform("settings/model-settings"),
        priority: HIGH_PRIORITY,
      });
    }
    if (user.featureFlags.enableApexPage) {
      result.push({
        id: "settings-apex",
        parent: "settings",
        name: "Apex",
        subtitle: "Manage Apex optimization North Star and Partners consent",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "apex partner consent platform",
        perform: () => perform("settings/apex"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.featureFlags.showDiscountCodeMappings) {
      result.push({
        id: "settings-discount-code-mappings",
        parent: "settings",
        name: "Discount code mappings",
        subtitle:
          "Create mappings between discount codes and custom campaign keys to better track discount code performance",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        keywords: "setting config discount code",
        perform: () => perform("settings/discount-code-mappings"),
        priority: HIGH_PRIORITY,
      });
    }

    if (user.isAdmin) {
      result.push({
        id: "settings-northbeam-labs",
        parent: "settings",
        name: "Northbeam labs",
        subtitle: "Manage Northbeam Feature Flags",
        section: { name: "Navigation", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-cog"></i>,
        perform: () => perform("settings/northbeam-labs"),
        priority: HIGH_PRIORITY,
      });

      result.push({
        id: "admin-modal",
        name: "Admin BQ SQL Diagnostics",
        section: { name: "Diagnostics", priority: HIGH_PRIORITY },
        icon: <i className="fa-regular fa-sharp fa-stethoscope"></i>,
        perform: () => setIsAdminModalOpen(true),
        priority: HIGH_PRIORITY,
        keywords: "admin sql diagnostics bq",
      });
    }

    return result;
  }, [
    user,
    isDarkMode,
    setIsDarkMode,
    navigate,
    openUniversalBenchmarksModal,
    setIsAdminModalOpen,
  ]);

  return (
    <CommandPaletteContext.Provider
      value={{ unregisterActionCallbacks, addUnregisterActionCallback }}
    >
      <KBarProvider
        actions={actions}
        options={{
          callbacks: {
            onOpen() {
              unregisterActionCallbacks();
            },
          },
        }}
      >
        <CommandDialog />
        {children}
      </KBarProvider>
    </CommandPaletteContext.Provider>
  );
};
